import React, { useEffect } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import {
  Route,
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  useRouteError,
} from 'react-router-dom'
import { theme as bsTheme } from 'bs-unified-ui'
import { Provider } from 'react-redux'
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles'
import { PersistGate } from 'redux-persist/integration/react'
import { FeaturesReady, GrowthBookProvider } from '@growthbook/growthbook-react'
import Hotjar from '@hotjar/browser'
import { createRoot } from 'react-dom/client'
import { setAuthTokenGetter, setBaseUrl } from '@bs/core'
import fbAuth from '~/modules/firebase'
import configureStore from '~/redux/store'
import AdminLayoutSelector from './layouts/AdminLayoutSelector'
import SystemRouters from './router/SystemRouters'
import '~/assets/css/style.scss'
import '@pantry/ui/src/styles/index.css'
import { PromptProvider } from '~/modules/Prompt'
import NoMatch from './pages/NoMatch'
import AuthRouters from './router/AuthRouters'
import ControlPanelRouters from './router/ControlPanelRouters'
import CustomRouters from './router/CustomRouters'
import FeatureFlagRoute from '~/components/main/routers/FeatureFlagRoute'
import '~/utils/datetime'
import RouteWithStore from '~/components/main/routers/RouteWithStore'
import 'react-datetime/css/react-datetime.css'
import DataLoading from './components/common/DataLoading'
import { appendPendo } from './utils/pendo'
import { appendGtag } from './utils/gaTag'
import {
  APP_SHA_VERSION,
  HOTJAR_SITE_ID,
  HOTJAR_VERSION,
  REACT_APP_MODE,
  BASE_URL,
} from './constants/environment'
import growthbook, { useLoadFeatures } from './configs/growthBook'
import theme from './themes/theme'
import WrapSSOProvider from './providers/WrapSSOProvider'
import { NuqsAdapter } from 'nuqs/adapters/react'
import ControlPannelLayout from './layouts/ControlPannel'

const { store, persistor } = configureStore()

if (BASE_URL) {
  setBaseUrl(BASE_URL)
}

setAuthTokenGetter(() => fbAuth.getToken())

const queryConfig = {
  queries: {
    refetchOnWindowFocus: false,
    retry: false,
  },
}

export const queryClient = new QueryClient({ defaultOptions: queryConfig })

let renderCount = 0

function FallbackComponent() {
  const error = useRouteError()

  // TODO: improve (remove when update createRoutesFromElements to useRoutes)
  // Stop endless loop in localhost mode
  if (REACT_APP_MODE === 'development') {
    const LOOP_ERRORS = [
      "Failed to execute 'insertBefore' on 'Node'",
      "Failed to execute 'removeChild' on 'Node'",
    ]

    renderCount += 1
    if (renderCount > 5) {
      throw new Error('Render loop detected')
    }

    if (LOOP_ERRORS.some((err) => error?.message?.includes(err))) {
      window.location.reload()
    }
  }
  // TODO: end

  if (error?.message?.includes('Failed to fetch dynamically imported module')) {
    window.location.reload()
  }
  return <div />
}

const myFallback = <FallbackComponent />

const App = () => {
  useEffect(() => {
    if (
      /(tablet|ipad|playbook|silk)|(android(?!.*mobi))|Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/i.test(
        navigator?.userAgent,
      )
    ) {
      document.body.classList.add('mobile-tablet-device')
    }
  }, [])

  useLoadFeatures()

  const appRouter = createBrowserRouter(
    createRoutesFromElements(
      <>
        {CustomRouters.map((router) => (
          <Route
            key={router.path}
            exact={router.exact}
            path={router.path}
            element={
              <React.Suspense fallback={<DataLoading loading />}>
                {router.layout ? (
                  <router.layout>
                    <router.component {...router.xProps} />
                  </router.layout>
                ) : (
                  <router.component {...router.xProps} />
                )}
              </React.Suspense>
            }
            errorElement={myFallback}
          />
        ))}
        {AuthRouters.map((router) => (
          <Route
            key={router.path}
            exact={router.exact}
            path={router.path}
            element={
              <React.Suspense fallback={<DataLoading loading />}>
                <router.layout>
                  <router.component {...router.xProps} />
                </router.layout>
              </React.Suspense>
            }
            errorElement={myFallback}
          />
        ))}
        {SystemRouters.filter(({ layout }) => layout === 'admin').map(
          (router) => (
            <Route
              key={router.path}
              exact={router.exact}
              path={`${router.path}`}
              element={
                <AdminLayoutSelector>
                  <React.Suspense fallback={<DataLoading loading />}>
                    <ThemeProvider theme={router.bsTheme ? bsTheme : theme}>
                      <RouteWithStore
                        key={router.path}
                        exact={router.exact}
                        path={`${router.path}`}
                        routeConfig={router}
                      />
                    </ThemeProvider>
                  </React.Suspense>
                </AdminLayoutSelector>
              }
              errorElement={myFallback}
            />
          ),
        )}
        {ControlPanelRouters.map((router) => (
          <Route
            key={router.path}
            exact={router.exact}
            path={router.path}
            element={
              <ControlPannelLayout>
                <React.Suspense fallback={<DataLoading loading />}>
                  <FeatureFlagRoute flag={router.featureFlag}>
                    {router.layout ? (
                      <router.layout>
                        <router.component {...router.xProps} />
                      </router.layout>
                    ) : (
                      <router.component {...router.xProps} />
                    )}
                  </FeatureFlagRoute>
                </React.Suspense>
              </ControlPannelLayout>
            }
            errorElement={myFallback}
          />
        ))}
        <Route path="*" element={<NoMatch />} />
      </>,
    ),
  )

  return (
    <GrowthBookProvider growthbook={growthbook}>
      <StyledEngineProvider injectFirst>
        <FeaturesReady fallback={null}>
          <ThemeProvider theme={bsTheme}>
            <Provider store={store}>
              <PersistGate loading={null} persistor={persistor}>
                <QueryClientProvider client={queryClient}>
                  <WrapSSOProvider>
                    <PromptProvider>
                      <NuqsAdapter>
                        <RouterProvider router={appRouter} />
                      </NuqsAdapter>
                    </PromptProvider>
                  </WrapSSOProvider>
                </QueryClientProvider>
              </PersistGate>
            </Provider>
            <div id="errorMessage" />
            <div id="toast" />
            <div id="app-version" style={{ display: 'none' }}>
              {APP_SHA_VERSION}
            </div>
          </ThemeProvider>
        </FeaturesReady>
      </StyledEngineProvider>
    </GrowthBookProvider>
  )
}

appendGtag()
appendPendo()

if (HOTJAR_SITE_ID) {
  Hotjar.init(HOTJAR_SITE_ID, HOTJAR_VERSION, {
    nonce: 'hotjar',
  })
}

const root = createRoot(document.getElementById('root'))
root.render(<App />)
